// ARP v2 — parse the CLI `--anchor` / `--rule` flags into the wire shapes the API
// expects. Kept pure + exported so it's unit-testable without the network.

const ANCHOR_META_KEYS = ['provider', 'auth', 'label', 'note', 'mode'] as const

export interface ParsedAnchor {
  type: string
  value: string
  [k: string]: string
}

// `--anchor type:value[:k=v,k=v]`. value may itself contain ':' and '/' (URLs,
// vault://…), so we DON'T naively split on ':' — we locate the meta segment by
// the first `:<known-key>=` marker; everything before it is the value.
export function parseAnchorFlag(raw: string): ParsedAnchor {
  const firstColon = raw.indexOf(':')
  if (firstColon <= 0) throw new Error(`--anchor must be "type:value[:meta]" (got "${raw}")`)
  const type = raw.slice(0, firstColon).trim()
  const rest = raw.slice(firstColon + 1)

  let metaIdx = -1
  for (const k of ANCHOR_META_KEYS) {
    const i = rest.indexOf(`:${k}=`)
    if (i >= 0 && (metaIdx < 0 || i < metaIdx)) metaIdx = i
  }
  const value = (metaIdx >= 0 ? rest.slice(0, metaIdx) : rest).trim()
  const anchor: ParsedAnchor = { type, value }
  if (metaIdx >= 0) {
    for (const pair of rest.slice(metaIdx + 1).split(',')) {
      const eq = pair.indexOf('=')
      if (eq > 0) anchor[pair.slice(0, eq).trim()] = pair.slice(eq + 1).trim()
    }
  }
  if (!value) throw new Error(`--anchor "${raw}" has no value`)
  // auth must be a reference, never an inline secret (the server enforces this too).
  if (anchor.auth && !/^vault:\/\//i.test(anchor.auth)) {
    throw new Error(`--anchor auth must be a vault:// reference, not an inline secret (got "${anchor.auth}")`)
  }
  return anchor
}

export function parseRuleFlag(raw: string): Record<string, unknown> {
  let obj: unknown
  try {
    obj = JSON.parse(raw)
  } catch {
    throw new Error(`--rule must be valid JSON (got "${raw.slice(0, 40)}…")`)
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) throw new Error('--rule must be a JSON object')
  const cond = (obj as Record<string, unknown>).condition
  if (typeof cond !== 'string' || !cond.trim()) throw new Error('--rule requires a non-empty "condition"')
  return obj as Record<string, unknown>
}

// file_pattern anchors flow into patterns[] (drift source of truth); everything
// else is an external anchor for anchors[].
export function splitAnchors(parsed: ParsedAnchor[]): { external: ParsedAnchor[]; filePatterns: string[] } {
  const external: ParsedAnchor[] = []
  const filePatterns: string[] = []
  for (const a of parsed) {
    if (a.type === 'file_pattern') filePatterns.push(a.value)
    else external.push(a)
  }
  return { external, filePatterns }
}

// `context search --type external` is the Directory view → the list endpoint with
// has_external=true (not the keyword search).
export function externalDirectoryPath(wsSlug: string): string {
  return `/workspaces/${wsSlug}/topics?has_external=true`
}
